import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  getSalesOrders,
  getSalesOrder,
  getSalesOrderTimeline,
  updateLots,
  getLotsByItem,
  getSOBatches,
  getBatchLots,
  saveBatchItemLots,
  getDeliveredLotsBySO,
  getDeliveredLotsByBatch,
  getCustomers,
  approveSalesOrderLots
} from "../../services/apiService";

import SoPrintTemplateLogistics from "./SOPrintTemplateLogistics";

import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import "./SalesOrdersLogistics.css";

const getUserRoleId = () => {
  let sessionUser = {};
  let localUser = {};

  try {
    sessionUser = JSON.parse(sessionStorage.getItem("user") || "{}");
  } catch {
    sessionUser = {};
  }

  try {
    localUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    localUser = {};
  }

  const user = Object.keys(sessionUser).length ? sessionUser : localUser;

  return Number(
    user.RoleID ??
    user.RoleId ??
    user.roleId ??
    user.roleID ??
    0
  );
};

const normalizeFilterValue = (value) => {
  return String(value ?? "").trim().toUpperCase();
};

const toDateInputValue = (value) => {
  if (!value) return "";

  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
};

const getSOCreatedDateValue = (so) => {
  return toDateInputValue(
    so?.CreatedAt ||
    so?.createdat ||
    so?.created_at ||
    so?.SODateTime ||
    so?.so_datetime ||
    so?.sodatetime ||
    so?.SODate ||
    so?.sodate
  );
};

const getLockedRowFlags = (detailId, rows, deliveredMap) => {
  const byDetail = deliveredMap?.[Number(detailId)] || {};
  const used = {};

  return (rows || []).map((r) => {
    const lot = String(r.LotNumber || "");
    const rowQty = Number(r.Qty || 1);

    if (!lot) return false;

    const deliveredQty = Number(byDetail[lot] || 0);
    const prev = Number(used[lot] || 0);

    const end = prev + rowQty;
    used[lot] = end;

    return deliveredQty >= end;
  });
};

const buildEmptyRows = (qty) =>
  Array(Number(qty || 0))
    .fill(null)
    .map(() => ({
      LotNumber: "",
      ExpiryDate: "",
      WarehouseID: "",
      WarehouseName: "",
      Qty: 1
    }));

const padLotRowsToQty = (savedRows, requiredQty) => {
  const safeQty = Number(requiredQty || 0);

  const rows = (savedRows || []).map((lot) => ({
    LotNumber: lot.LotNumber || "",
    ExpiryDate: lot.ExpiryDate || "",
    WarehouseID: lot.WarehouseID || "",
    WarehouseName: lot.WarehouseName || "",
    Qty: Number(lot.Qty || 1),
  }));

  while (rows.length < safeQty) {
    rows.push({
      LotNumber: "",
      ExpiryDate: "",
      WarehouseID: "",
      WarehouseName: "",
      Qty: 1,
    });
  }

  return rows.slice(0, safeQty);
};

const normalizeLotRowsForSave = (rows) =>
  (rows || [])
    .filter((lot) => String(lot?.LotNumber || "").trim() !== "")
    .map((lot) => ({
      LotNumber: String(lot.LotNumber || "").trim(),
      ExpiryDate: lot.ExpiryDate || "",
      WarehouseID: lot.WarehouseID || "",
      WarehouseName: lot.WarehouseName || "",
      Qty: Number(lot.Qty || 1),
    }));

// const ensureRows = (existingRows, qty) =>
//   existingRows && existingRows.length ? existingRows : buildEmptyRows(qty);

const getSOStatus = (so) =>
  String(
    so?.SOStatus ||
    so?.sostatus ||
    so?.DeliveryStatus ||
    so?.deliverystatus ||
    ""
  ).toUpperCase();

const getApprovalStatus = (so) =>
  String(so?.ApprovalStatus || so?.approvalstatus || "").toUpperCase();

const isApprovedSO = (so) => getApprovalStatus(so) === "APPROVED";

const isCompleteSO = (so) => getSOStatus(so) === "COMPLETE";

const getLotApprovalStatus = (so) =>
  String(
    so?.LotApprovalStatus ||
    so?.lotapprovalstatus ||
    so?.lotApprovalStatus ||
    "PENDING"
  ).toUpperCase();

const isLotApproved = (so) => getLotApprovalStatus(so) === "APPROVED";

const lotApprovalStatusLabel = (value) => {
  const key = String(value || "").toUpperCase();

  const map = {
    PENDING: "Pending",
    APPROVED: "Approved",
    CANCELLED: "Cancelled"
  };

  return map[key] || value || "-";
};

const unitLabel = (u) => {
  const key = String(u || "").trim().toUpperCase();
  const map = {
    PC: "PC",
    PCS: "PCS",
    PIECE: "PC",
    VIAL: "VIAL",
    BOX: "BOX",
    PACK: "PACK",
    KIT: "KIT",
    SET: "SET"
  };
  return map[key] || String(u || "").toLowerCase() || "pc";
};

const isCancelledSO = (so) =>
  ["CANCEL", "CANCELLED"].includes(getSOStatus(so));

const isReturnedSO = (so) => getSOStatus(so) === "RETURNED";

const getMode = (so) =>
  String(so?.DeliveryMode || so?.delivery_mode || "SINGLE").toUpperCase();

const buildDeliveredMap = (rows) => {
  const map = {};
  for (const r of rows || []) {
    const dId = Number(r.SODetailID);
    const lot = String(r.LotNumber || "");
    const safeQty = Number(r.DeliveredQty || 0);

    if (!dId || !lot || safeQty <= 0) continue;
    if (!map[dId]) map[dId] = {};
    map[dId][lot] = (map[dId][lot] || 0) + safeQty;
  }
  return map;
};

const pickDefaultBatch = (list) => {
  if (!list || list.length === 0) return null;
  const curMonth = new Date().toISOString().slice(0, 7);
  const matchCur = list.find((b) => String(b.PlannedMonth) === String(curMonth));
  return matchCur || list[0];
};

const getStatusClass = (value) => {
  const key = String(value || "").toUpperCase();

  if (key === "PENDING") return "status-pill pending";
  if (key === "ONGOING") return "status-pill ongoing";
  if (key === "PARTIAL") return "status-pill partial";
  if (key === "COMPLETE") return "status-pill complete";
  if (key === "CANCEL" || key === "CANCELLED") return "status-pill cancelled";
  if (key === "RETURNED") return "status-pill returned";
  if (key === "APPROVED") return "status-pill complete";
  if (key === "DISAPPROVED") return "status-pill cancelled";
  if (key === "ONHOLD") return "status-pill onhold";

  return "status-pill";
};

const approvalStatusLabel = (value) => {
  const key = String(value || "").toUpperCase();
  const map = {
    PENDING: "Pending",
    ONHOLD: "On Hold",
    APPROVED: "Approved",
    // DISAPPROVED: "Disapproved"
  };
  return map[key] || value || "-";
};

const soStatusLabel = (v) => {
  const map = {
    PENDING: "Pending",
    ONGOING: "Ongoing",
    PARTIAL: "Partial",
    COMPLETE: "Complete",
    CANCEL: "Cancelled",
    CANCELLED: "Cancelled",
    RETURNED: "Returned"
  };

  const key = String(v || "").toUpperCase();
  return map[key] || v || "-";
};

export default function SalesOrdersLogistics() {
  const roleId = getUserRoleId();

  const printPreviewOnlyRoleIds = [5, 7, 8, 15];
  const lotApprovalRoleIds = [15];

  const isPrintPreviewOnlyRole = printPreviewOnlyRoleIds.includes(roleId);
  const canSeeLotApproval = lotApprovalRoleIds.includes(roleId);

  // Keep this variable so your existing code will still work
  const isRole8PrintPreviewOnly = isPrintPreviewOnlyRole;

  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedSO, setSelectedSO] = useState(null);
  const [items, setItems] = useState([]);
  const [showPrintPreview, setShowPrintPreview] = useState(false);

  const [isEditing, setIsEditing] = useState(true);

  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [selectedBatch, setSelectedBatch] = useState(null);

  const [deliveredMap, setDeliveredMap] = useState({});

  const [showCustomerDetailsModal, setShowCustomerDetailsModal] = useState(false);
  const [selectedCustomerDetails, setSelectedCustomerDetails] = useState(null);

  const [deliveryStatusFilter, setDeliveryStatusFilter] = useState("ALL");
  const [lotApprovalFilter, setLotApprovalFilter] = useState("ALL");

  const [tableSearch, setTableSearch] = useState("");
  const [createdDateFrom, setCreatedDateFrom] = useState("");
  const [createdDateTo, setCreatedDateTo] = useState("");
  const [createdByFilter, setCreatedByFilter] = useState("ALL");
  const [approvalStatusFilter, setApprovalStatusFilter] = useState("ALL");
  const [soStatusFilter, setSOStatusFilter] = useState("ALL");
  const [orderTypeFilter, setOrderTypeFilter] = useState("ALL");
  const [deliveryModeFilter, setDeliveryModeFilter] = useState("ALL");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);

  const [lotApprovalLoading, setLotApprovalLoading] = useState(false);
  const [showLotApprovalConfirm, setShowLotApprovalConfirm] = useState(false);

  const [soTimeline, setSoTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [showTimelineModal, setShowTimelineModal] = useState(false);

  const printRef = useRef(null);

  const isLocked = selectedSO
    ? isCompleteSO(selectedSO) ||
    isCancelledSO(selectedSO) ||
    isReturnedSO(selectedSO)
    : false;

  const isApprovalLocked = selectedSO ? !isApprovedSO(selectedSO) : false;

  const canEditLots = selectedSO
    ? !isRole8PrintPreviewOnly && !isLocked && !isApprovalLocked && isEditing
    : false;

  const selectedLotApprovalStatus = selectedSO
    ? getLotApprovalStatus(selectedSO)
    : "PENDING";

  const canApproveLot =
    selectedSO &&
    canSeeLotApproval &&
    isApprovedSO(selectedSO) &&
    !isLocked &&
    !isLotApproved(selectedSO);

  const handleApproveLots = async () => {
    if (!selectedSO) return;

    if (!canSeeLotApproval) {
      alert("Only the Lot Approval role can approve Lot #.");
      return;
    }

    if (!isApprovedSO(selectedSO)) {
      alert("Cannot approve Lot #. Sales Order is not yet approved.");
      return;
    }

    if (isCancelledSO(selectedSO)) {
      alert("Cannot approve Lot #. Sales Order is cancelled.");
      return;
    }

    if (isCompleteSO(selectedSO)) {
      alert("Cannot approve Lot #. Sales Order is already complete.");
      return;
    }

    if (isReturnedSO(selectedSO)) {
      alert("Cannot approve Lot #. Sales Order is returned.");
      return;
    }

    if (isLotApproved(selectedSO)) {
      alert("Lot # is already approved.");
      return;
    }

    if (selectedLotRows.length === 0) {
      alert("Cannot approve Lot #. Please input/select at least one Lot # first.");
      return;
    }

    setShowLotApprovalConfirm(true);
  };

  // const confirmApproveLots = async () => {
  //   if (!selectedSO) return;

  //   if (selectedLotRows.length === 0) {
  //     alert("Cannot approve Lot #. Please input/select at least one Lot # first.");
  //     setShowLotApprovalConfirm(false);
  //     return;
  //   }

  //   try {
  //     setLotApprovalLoading(true);

  //     const soid = selectedSO.SOID || selectedSO.soid;

  //     await approveSalesOrderLots(soid, "");

  //     const refreshed = await getSalesOrder(soid);

  //     const updatedSO = {
  //       ...refreshed,
  //       LotApprovalStatus: refreshed?.LotApprovalStatus || "APPROVED",
  //       lotapprovalstatus: refreshed?.lotapprovalstatus || "APPROVED",
  //     };

  //     setSelectedSO(updatedSO);

  //     setSalesOrders((prev) =>
  //       (prev || []).map((so) => {
  //         const rowId = so.SOID || so.soid;
  //         if (Number(rowId) !== Number(soid)) return so;

  //         return {
  //           ...so,
  //           LotApprovalStatus: "APPROVED",
  //           lotapprovalstatus: "APPROVED",
  //           lotApprovedBy:
  //             updatedSO.LotApprovedBy ||
  //             updatedSO.lotapprovedby ||
  //             so.lotApprovedBy ||
  //             so.lotapprovedby,
  //           lotapprovedby:
  //             updatedSO.LotApprovedBy ||
  //             updatedSO.lotapprovedby ||
  //             so.lotApprovedBy ||
  //             so.lotapprovedby,
  //         };
  //       })
  //     );

  //     await loadSalesOrders();
  //     setShowLotApprovalConfirm(false);
  //   } catch (err) {
  //     alert(
  //       err?.response?.data?.error ||
  //       err?.message ||
  //       "Failed to approve Lot #."
  //     );
  //   } finally {
  //     setLotApprovalLoading(false);
  //   }
  // };

  const confirmApproveLots = async () => {
    if (!selectedSO) return;

    if (selectedLotRows.length === 0) {
      alert("Cannot approve Lot #. Please input/select at least one Lot # first.");
      setShowLotApprovalConfirm(false);
      return;
    }

    const mode = getMode(selectedSO);
    const isStaggered = mode === "STAGGERED";

    if (isStaggered && !selectedBatchId) {
      alert("Please select a Batch first before approving Lot #.");
      setShowLotApprovalConfirm(false);
      return;
    }

    try {
      setLotApprovalLoading(true);

      const soid = selectedSO.SOID || selectedSO.soid;
      const batchId = isStaggered ? Number(selectedBatchId) : null;

      /*
        Important:
        For staggered, save the currently selected batch lots first
        before approval, so backend can validate existing lots.
      */
      if (isStaggered) {
        for (const it of items) {
          const lotsToSave = normalizeLotRowsForSave(it.lots || []);
          await saveBatchItemLots(batchId, it.SODetailID, lotsToSave);
        }

        await loadBatchIntoItems(batchId, items);
      }

      await approveSalesOrderLots(soid, {
        remarks: "",
        batchId,
        lots: selectedLotRows,
        deliveryMode: mode,
      });

      const refreshed = await getSalesOrder(soid);

      const updatedSO = {
        ...refreshed,
        LotApprovalStatus: refreshed?.LotApprovalStatus || "APPROVED",
        lotapprovalstatus: refreshed?.lotapprovalstatus || "APPROVED",
      };

      setSelectedSO(updatedSO);

      setSalesOrders((prev) =>
        (prev || []).map((so) => {
          const rowId = so.SOID || so.soid;
          if (Number(rowId) !== Number(soid)) return so;

          return {
            ...so,
            LotApprovalStatus: "APPROVED",
            lotapprovalstatus: "APPROVED",
            lotApprovedBy:
              updatedSO.LotApprovedBy ||
              updatedSO.lotapprovedby ||
              so.lotApprovedBy ||
              so.lotapprovedby,
            lotapprovedby:
              updatedSO.LotApprovedBy ||
              updatedSO.lotapprovedby ||
              so.lotApprovedBy ||
              so.lotapprovedby,
          };
        })
      );

      await loadSalesOrders();
      setShowLotApprovalConfirm(false);

      alert("Lot # approved successfully.");
    } catch (err) {
      alert(
        err?.response?.data?.error ||
        err?.message ||
        "Failed to approve Lot #."
      );
    } finally {
      setLotApprovalLoading(false);
    }
  };

  const getSOCreatedByName = (so) => {
    return (
      String(
        so.CreatedByName ??
        so.createdbyname ??
        so.CreatedBy ??
        so.createdby ??
        so.PreparedBy ??
        so.preparedby ??
        so.PendingBy ??
        so.pendingby ??
        ""
      ).trim() || "-"
    );
  };

  const getValue = (obj, keys, fallback = "-") => {
    for (const key of keys) {
      const value = obj?.[key];

      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return value;
      }
    }

    return fallback;
  };

  const getCustomerDetailsFromSO = (so) => {
    const soCustomerId = getValue(
      so,
      ["CustomerID", "customer_id", "customerid", "SoldTo", "sold_to"],
      ""
    );

    const soCustomerName = getValue(
      so,
      ["CustomerName", "customername", "customer_name"],
      ""
    );

    const matchedCustomerById = customers.find((c) => {
      const customerId = getValue(
        c,
        ["customer_id", "CustomerID", "CustomerId", "id"],
        ""
      );

      return (
        soCustomerId &&
        customerId &&
        String(customerId) === String(soCustomerId)
      );
    });

    const matchedCustomerByName = customers.find((c) => {
      const customerName = getValue(
        c,
        ["customer_name", "CustomerName", "customername"],
        ""
      );

      return (
        soCustomerName &&
        customerName &&
        String(customerName).trim().toLowerCase() ===
        String(soCustomerName).trim().toLowerCase()
      );
    });

    return (
      matchedCustomerById ||
      matchedCustomerByName || {
        customer_name: soCustomerName,
        customer_number: getValue(
          so,
          ["CustomerNumber", "customer_number", "Customer_Number"],
          ""
        ),
        ship_to: getValue(so, ["ShipTo", "shipto", "ship_to"], ""),
        terms: getValue(so, ["Terms", "terms", "terms_of_payment"], "")
      }
    );
  };

  const openCustomerDetailsModal = (so) => {
    const details = getCustomerDetailsFromSO(so);
    setSelectedCustomerDetails(details);
    setShowCustomerDetailsModal(true);
  };

  const closeCustomerDetailsModal = () => {
    setShowCustomerDetailsModal(false);
    setSelectedCustomerDetails(null);
  };

  const filteredSalesOrders = useMemo(() => {
    const keyword = tableSearch.trim().toLowerCase();

    return (salesOrders || []).filter((so) => {
      const soNumber = so.sonumber || so.SONumber || "";
      const customer = so.customername || so.CustomerName || "";
      const shipTo = so.shipto || so.ShipTo || "";
      const createdBy = getSOCreatedByName(so);
      const createdDateValue = getSOCreatedDateValue(so);

      const orderType = normalizeFilterValue(
        so.ordertype || so.OrderType || "NORMAL"
      );

      const deliveryMode = normalizeFilterValue(
        so.deliverymode || so.DeliveryMode || "SINGLE"
      );

      const approvalStatus = normalizeFilterValue(
        so.approvalstatus || so.ApprovalStatus || "PENDING"
      );

      const lotApprovalStatus = normalizeFilterValue(
        so.lotapprovalstatus ||
        so.LotApprovalStatus ||
        so.lotApprovalStatus ||
        "PENDING"
      );

      const soStatus = normalizeFilterValue(
        so.sostatus || so.SOStatus || "PENDING"
      );

      const deliveryStatus = normalizeFilterValue(
        so.deliverystatus || so.DeliveryStatus || "PENDING"
      );

      const searchableOrderType = orderType.replace(/_/g, " ");
      const searchableDeliveryMode = deliveryMode.replace(/_/g, " ");

      const matchesSearch =
        !keyword ||
        `${soNumber} ${customer} ${createdBy} ${shipTo} ${orderType} ${searchableOrderType} ${deliveryMode} ${searchableDeliveryMode} ${approvalStatus} ${lotApprovalStatus} ${soStatus} ${deliveryStatus}`
          .toLowerCase()
          .includes(keyword);

      const matchesCreatedDateFrom =
        !createdDateFrom ||
        (createdDateValue && createdDateValue >= createdDateFrom);

      const matchesCreatedDateTo =
        !createdDateTo ||
        (createdDateValue && createdDateValue <= createdDateTo);

      const matchesCreatedBy =
        createdByFilter === "ALL" ||
        createdBy.toLowerCase() === createdByFilter.toLowerCase();

      const matchesApprovalStatus =
        approvalStatusFilter === "ALL" ||
        approvalStatus === approvalStatusFilter;

      const matchesLotApprovalStatus =
        lotApprovalFilter === "ALL" ||
        (lotApprovalFilter === "CANCELLED"
          ? lotApprovalStatus === "CANCELLED" || lotApprovalStatus === "CANCEL"
          : lotApprovalStatus === lotApprovalFilter);

      const matchesSOStatus =
        soStatusFilter === "ALL" ||
        (soStatusFilter === "CANCELLED"
          ? soStatus === "CANCELLED" || soStatus === "CANCEL"
          : soStatus === soStatusFilter);

      const matchesDeliveryStatus =
        deliveryStatusFilter === "ALL" ||
        (deliveryStatusFilter === "CANCELLED"
          ? deliveryStatus === "CANCELLED" || deliveryStatus === "CANCEL"
          : deliveryStatus === deliveryStatusFilter);

      const matchesOrderType =
        orderTypeFilter === "ALL" || orderType === orderTypeFilter;

      const matchesDeliveryMode =
        deliveryModeFilter === "ALL" || deliveryMode === deliveryModeFilter;

      return (
        matchesSearch &&
        matchesCreatedDateFrom &&
        matchesCreatedDateTo &&
        matchesCreatedBy &&
        matchesApprovalStatus &&
        matchesLotApprovalStatus &&
        matchesSOStatus &&
        matchesDeliveryStatus &&
        matchesOrderType &&
        matchesDeliveryMode
      );
    });
  }, [
    salesOrders,
    tableSearch,
    createdDateFrom,
    createdDateTo,
    createdByFilter,
    approvalStatusFilter,
    lotApprovalFilter,
    soStatusFilter,
    deliveryStatusFilter,
    orderTypeFilter,
    deliveryModeFilter
  ]);

  const openPrintPreview = () => {
    setShowPrintPreview(true);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("SET_COPY_TYPE", { detail: "LOGISTICS" })
      );
    }, 200);
  };

  const loadSalesOrders = useCallback(async () => {
    const data = await getSalesOrders();
    setSalesOrders(data || []);

    try {
      const customerData = await getCustomers();
      setCustomers(customerData || []);
    } catch (err) {
      console.error("Error loading customer details:", err);
    }
  }, []);

  const loadBatchIntoItems = useCallback(async (batchId, base) => {
    const rows = await getBatchLots(batchId);

    const grouped = new Map();
    for (const r of rows || []) {
      const key = Number(r.SODetailID);
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key).push({
        LotNumber: r.LotNumber || "",
        ExpiryDate: r.ExpiryDate || "",
        WarehouseID: r.WarehouseID || "",
        WarehouseName: r.WarehouseName || "",
        Qty: Number(r.Qty || 1)
      });
    }

    const updated = (base || []).map((it) => {
      const lots = grouped.get(Number(it.SODetailID)) || [];
      const batchQty = lots.reduce((sum, l) => sum + Number(l.Qty || 1), 0);
      return { ...it, batchQty, lots };
    });

    setItems(updated);
  }, []);

  const loadSOTimeline = useCallback(async (soid) => {
    if (!soid) {
      setSoTimeline([]);
      return;
    }

    try {
      setTimelineLoading(true);
      const rows = await getSalesOrderTimeline(soid);
      setSoTimeline(rows || []);
    } catch (err) {
      console.error("Load SO timeline error:", err);
      setSoTimeline([]);
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSalesOrders();
  }, [loadSalesOrders]);

  useEffect(() => {
    setPage(1);
  }, [
    tableSearch,
    createdDateFrom,
    createdDateTo,
    createdByFilter,
    approvalStatusFilter,
    lotApprovalFilter,
    soStatusFilter,
    deliveryStatusFilter,
    orderTypeFilter,
    deliveryModeFilter,
    rowsPerPage
  ]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil((filteredSalesOrders?.length || 0) / rowsPerPage));
    if (page > totalPages) setPage(totalPages);
    if (page < 1) setPage(1);
  }, [filteredSalesOrders, rowsPerPage, page]);

  const viewSO = async (id) => {
    const data = await getSalesOrder(id);
    const mode = getMode(data);
    const isStaggered = mode === "STAGGERED";

    setSelectedSO(data);
    setShowPrintPreview(false);
    setDeliveredMap({});
    setSoTimeline([]);
    setShowTimelineModal(false);

    if (
      isRole8PrintPreviewOnly ||
      isCompleteSO(data) ||
      isCancelledSO(data) ||
      isReturnedSO(data) ||
      !isApprovedSO(data)
    ) {
      setIsEditing(false);
    } else {
      setIsEditing(true);
    }

    const baseItems = (data.items || []).map((it) => {
      const fullQty = Number(it.Quantity || 0);

      return {
        ...it,
        fullQty,
        batchQty: 0,
        lots: [],
      };
    });

    setItems(baseItems);

    const soid = data.SOID || data.soid;

    if (isStaggered) {
      const b = await getSOBatches(soid);
      const list = b || [];
      setBatches(list);

      if (list.length > 0) {
        const def = pickDefaultBatch(list);

        setSelectedBatchId(String(def.BatchID));
        setSelectedBatch(def);

        await loadBatchIntoItems(def.BatchID, baseItems);

        const deliveredRows = await getDeliveredLotsByBatch(def.BatchID);
        setDeliveredMap(buildDeliveredMap(deliveredRows));

        if (
          isRole8PrintPreviewOnly ||
          isCompleteSO(data) ||
          isCancelledSO(data) ||
          isReturnedSO(data) ||
          !isApprovedSO(data)
        ) {
          setIsEditing(false);
        } else {
          const existingLots = await getBatchLots(def.BatchID);
          setIsEditing(!(existingLots && existingLots.length > 0));
        }
      } else {
        setSelectedBatchId("");
        setSelectedBatch(null);

        setIsEditing(
          !(
            isRole8PrintPreviewOnly ||
            isCompleteSO(data) ||
            isCancelledSO(data) ||
            isReturnedSO(data) ||
            !isApprovedSO(data)
          )
        );
      }
    } else {
      const deliveredRows = await getDeliveredLotsBySO(soid);
      setDeliveredMap(buildDeliveredMap(deliveredRows));

      const mapped = (data.items || []).map((it) => {
        const fullQty = Number(it.Quantity || 0);
        const rows = padLotRowsToQty(it.lots || [], fullQty);

        return {
          ...it,
          fullQty,
          lots: rows,
        };
      });

      const hasAnySavedLots = mapped.some((it) =>
        (it.lots || []).some(
          (lot) => String(lot.LotNumber || "").trim() !== ""
        )
      );

      setItems(mapped);

      if (
        isRole8PrintPreviewOnly ||
        isCompleteSO(data) ||
        isCancelledSO(data) ||
        isReturnedSO(data) ||
        !isApprovedSO(data)
      ) {
        setIsEditing(false);
      } else {
        setIsEditing(!hasAnySavedLots);
      }
    }
  };

  const handleSelectBatch = async (batchIdStr) => {
    setSelectedBatchId(batchIdStr);

    const batch =
      (batches || []).find((x) => String(x.BatchID) === String(batchIdStr)) || null;
    setSelectedBatch(batch);

    if (!batchIdStr) {
      setItems((prev) => prev.map((it) => ({ ...it, batchQty: 0, lots: [] })));
      setDeliveredMap({});
      setIsEditing(false);
      return;
    }

    const bid = Number(batchIdStr);
    await loadBatchIntoItems(bid, items);

    const deliveredRows = await getDeliveredLotsByBatch(bid);
    setDeliveredMap(buildDeliveredMap(deliveredRows));

    if (
      isRole8PrintPreviewOnly ||
      (
        selectedSO &&
        (
          isCompleteSO(selectedSO) ||
          isCancelledSO(selectedSO) ||
          isReturnedSO(selectedSO) ||
          !isApprovedSO(selectedSO)
        )
      )
    ) {
      setIsEditing(false);
      setShowPrintPreview(false);
      return;
    }

    const existingLots = await getBatchLots(bid);
    setIsEditing(!(existingLots && existingLots.length > 0));

    setShowPrintPreview(false);
  };

  const handleBatchQtyChange = (detailId, newQty) => {
    if (!canEditLots) return;

    setItems((prev) =>
      prev.map((it) => {
        if (it.SODetailID !== detailId) return it;

        const fullQty = Number(it.Quantity || 0);
        const safeQty = Math.max(0, Math.min(Number(newQty || 0), fullQty));

        const existing = it.lots || [];
        const next = buildEmptyRows(safeQty).map((row, idx) => {
          const old = existing[idx];
          return old
            ? {
              ...row,
              LotNumber: old.LotNumber || "",
              ExpiryDate: old.ExpiryDate || "",
              WarehouseID: old.WarehouseID || "",
              WarehouseName: old.WarehouseName || "",
              Qty: old.Qty || 1
            }
            : row;
        });

        return { ...it, batchQty: safeQty, lots: next };
      })
    );
  };

  const handleLotUpdate = useCallback(
    (detailId, updatedLots) => {
      if (!canEditLots) return;

      setItems((prev) =>
        prev.map((it) =>
          it.SODetailID === detailId
            ? {
              ...it,
              lots: updatedLots,
              batchQty: updatedLots.reduce((s, l) => s + Number(l.Qty || 1), 0)
            }
            : it
        )
      );
    },
    [canEditLots]
  );

  const saveChanges = async () => {
    if (!selectedSO) return;

    if (isRole8PrintPreviewOnly) {
      alert("This role is Print Preview only.");
      return;
    }

    if (!isApprovedSO(selectedSO)) {
      alert("This Sales Order is still waiting for Accounting Approval.");
      return;
    }

    if (
      isCompleteSO(selectedSO) ||
      isCancelledSO(selectedSO) ||
      isReturnedSO(selectedSO)
    ) {
      alert("This Sales Order is LOCKED (COMPLETE/CANCEL/RETURNED). Editing is disabled.");
      return;
    }

    const mode = getMode(selectedSO);
    const isStaggered = mode === "STAGGERED";

    if (isStaggered) {
      if (!selectedBatchId) {
        alert("Please select a Batch first.");
        return;
      }

      for (const it of items) {
        const qty = Number(it.batchQty || 0);
        if (qty === 0) continue;

        if (!it.lots || it.lots.length !== qty) {
          alert(
            `Mismatch rows for ${it.ItemName}.\nBatch Qty: ${qty}\nLot rows: ${it.lots?.length || 0
            }`
          );
          return;
        }
      }

      const batchId = Number(selectedBatchId);

      for (const it of items) {
        const lotsToSave = normalizeLotRowsForSave(it.lots || []);
        await saveBatchItemLots(batchId, it.SODetailID, lotsToSave);
      }

      alert("Batch lots saved successfully.");

      setIsEditing(false);

      await loadBatchIntoItems(batchId, items);

      const deliveredRows = await getDeliveredLotsByBatch(batchId);
      setDeliveredMap(buildDeliveredMap(deliveredRows));

      await loadSalesOrders();

      const refreshed = await getSalesOrder(selectedSO.SOID || selectedSO.soid);
      setSelectedSO(refreshed);
      return;
    }

    for (const it of items) {
      const fullQty = Number(it.Quantity || 0);

      if (!it.lots || it.lots.length !== fullQty) {
        alert(
          `Mismatch rows for ${it.ItemName}.\nRequired Qty: ${fullQty}\nLot rows: ${it.lots?.length || 0
          }`
        );
        return;
      }
    }

    for (const it of items) {
      const lotsToSave = normalizeLotRowsForSave(it.lots || []);
      await updateLots(it.SODetailID, lotsToSave);
    }

    alert("LOTS saved successfully!");

    await loadSalesOrders();

    const refreshed = await getSalesOrder(selectedSO.SOID || selectedSO.soid);
    setSelectedSO(refreshed);
    const refreshedMapped = (refreshed.items || []).map((it) => {
      const fullQty = Number(it.Quantity || 0);
      const rows = padLotRowsToQty(it.lots || [], fullQty);

      return {
        ...it,
        fullQty,
        lots: rows,
      };
    });

    setItems(refreshedMapped);

    const deliveredRows = await getDeliveredLotsBySO(
      refreshed.SOID || refreshed.soid
    );
    setDeliveredMap(buildDeliveredMap(deliveredRows));

    setIsEditing(false);
  };

  const clearLots = async () => {
    if (!selectedSO) return;

    if (isRole8PrintPreviewOnly) {
      alert("This role is Print Preview only.");
      return;
    }

    if (!isApprovedSO(selectedSO)) {
      alert("This Sales Order is still waiting for Accounting Approval.");
      return;
    }

    if (
      isCompleteSO(selectedSO) ||
      isCancelledSO(selectedSO) ||
      isReturnedSO(selectedSO)
    ) {
      alert("CANCEL/COMPLETE/RETURNED SO cannot be cleared.");
      return;
    }

    const ok = window.confirm(
      "Clear lots for this Sales Order? (This will SAVE immediately)"
    );

    if (!ok) return;

    const mode = getMode(selectedSO);
    const isStaggered = mode === "STAGGERED";

    if (isStaggered) {
      if (!selectedBatchId) {
        alert("Please select a Batch first.");
        return;
      }

      const batchId = Number(selectedBatchId);

      const newItems = items.map((it) => {
        const qty = Number(it.batchQty || 0);
        const baseRows = padLotRowsToQty(it.lots || [], qty);

        const lockedFlags = getLockedRowFlags(
          it.SODetailID,
          baseRows,
          deliveredMap
        );

        const nextLots = baseRows.map((lot, idx) => {
          if (lockedFlags[idx]) return lot;

          return {
            ...lot,
            LotNumber: "",
            ExpiryDate: "",
            WarehouseID: "",
            WarehouseName: "",
          };
        });

        const nextBatchQty = nextLots.reduce(
          (sum, lot) => sum + Number(lot.Qty || 1),
          0
        );

        return {
          ...it,
          lots: nextLots,
          batchQty: nextBatchQty,
        };
      });

      setItems(newItems);
      setIsEditing(true);

      for (const it of newItems) {
        const lotsToSave = normalizeLotRowsForSave(it.lots || []);
        await saveBatchItemLots(batchId, it.SODetailID, lotsToSave);
      }

      alert("Cleared only UNDELIVERED rows for this batch — saved.");
      return;
    }

    const newItems = items.map((it) => {
      const fullQty = Number(it.Quantity || 0);
      const baseRows = padLotRowsToQty(it.lots || [], fullQty);

      const lockedFlags = getLockedRowFlags(
        it.SODetailID,
        baseRows,
        deliveredMap
      );

      const nextLots = baseRows.map((lot, idx) => {
        if (lockedFlags[idx]) return lot;

        return {
          ...lot,
          LotNumber: "",
          ExpiryDate: "",
          WarehouseID: "",
          WarehouseName: "",
        };
      });

      return {
        ...it,
        lots: nextLots,
      };
    });

    setItems(newItems);
    setIsEditing(true);

    for (const it of newItems) {
      const lotsToSave = normalizeLotRowsForSave(it.lots || []);
      await updateLots(it.SODetailID, lotsToSave);
    }

    alert("Cleared only UNDELIVERED rows — saved.");
  };

  const formatSODateTime = (so) => {
    const dt = so?.so_datetime || so?.SODateTime || so?.sodatetime || null;

    if (!dt) {
      const d = so?.sodate || so?.SODate;
      const c = so?.createdat || so?.CreatedAt;

      if (!d) return "-";

      const soDate = new Date(d);
      const created = c ? new Date(c) : null;

      if (created) {
        const merged = new Date(
          soDate.getFullYear(),
          soDate.getMonth(),
          soDate.getDate(),
          created.getHours(),
          created.getMinutes(),
          created.getSeconds()
        );

        return merged.toLocaleString("en-PH", {
          year: "numeric",
          month: "short",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true
        });
      }

      return soDate.toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "2-digit"
      });
    }

    return new Date(dt).toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  };

  const downloadThreeCopiesPDF = async () => {
    if (!printRef.current) return;

    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;

    const copyTypes = ["OFFICE FILE", "ACCOUNTING COPY", "LOGISTIC COPY"];

    for (let i = 0; i < copyTypes.length; i++) {
      window.dispatchEvent(new CustomEvent("SET_COPY_TYPE", { detail: copyTypes[i] }));
      await new Promise((r) => setTimeout(r, 300));

      const element = printRef.current;
      const canvas = await html2canvas(element, { scale: 3 });
      const imgData = canvas.toDataURL("image/png");
      const imgHeight = (canvas.height * usableWidth) / canvas.width;

      if (i > 0) doc.addPage();
      doc.addImage(imgData, "PNG", margin, margin, usableWidth, imgHeight);
    }

    const mode = getMode(selectedSO);
    const suffix =
      mode === "STAGGERED" && selectedBatch?.BatchNo ? `_BATCH-${selectedBatch.BatchNo}` : "";

    doc.save(`${selectedSO?.SONumber || "SO"}_LOGISTICS${suffix}.pdf`);
  };

  const orderTypeLabel = (v) => {
    const map = {
      NORMAL: "Normal",
      DEMO: "Demo",
      FREE_GOODS: "Free Goods",
      BIOCARE_ACCOUNT: "Biocare Account",
      REPLACEMENT: "Replacement",
      AS_TESTING: "AS Testing",
      TSD_TESTING: "TSD Testing",
      MARKETING_EVENT: "Marketing Event"
    };
    return map[v] || v || "-";
  };

  const deliveryModeLabel = (v) => {
    const map = { SINGLE: "Single", STAGGERED: "Staggered" };
    return map[v] || v || "-";
  };

  const selectedDeliveryMode = getMode(selectedSO);
  const isSelectedStaggered = selectedDeliveryMode === "STAGGERED";

  const lockedReason = (() => {
    if (!selectedSO) return "";
    if (!isApprovedSO(selectedSO)) return "WAITING FOR ACCOUNTING APPROVAL — Lot assignment is disabled.";
    if (isCancelledSO(selectedSO)) return "CANCELLED — Editing is disabled.";
    if (isCompleteSO(selectedSO)) return "COMPLETE — Editing is disabled.";
    if (isReturnedSO(selectedSO)) return "RETURNED — Editing is disabled.";
    return "";
  })();

  const totalRows = filteredSalesOrders?.length || 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const pagedSalesOrders = (filteredSalesOrders || []).slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  const pageInfo = totalRows
    ? `${(page - 1) * rowsPerPage + 1}-${Math.min(page * rowsPerPage, totalRows)} of ${totalRows}`
    : "0 of 0";

  const selectedLotRows = useMemo(() => {
    const rows = [];

    (items || []).forEach((item) => {
      const itemName =
        item.ItemName ||
        item.CurrentItemName ||
        item.item_name ||
        item.Description ||
        "-";

      const catalogNo =
        item.CatalogNo ||
        item.CurrentCatalogNo ||
        item.catalog_number ||
        item.CatalogNumber ||
        "";

      const lots = item.lots || [];

      // This will detect which lot rows are already delivered.
      const lockedFlags = getLockedRowFlags(
        item.SODetailID,
        lots,
        deliveredMap
      );

      (lots || []).forEach((lot, index) => {
        const lotNumber = String(lot.LotNumber || "").trim();

        if (!lotNumber) return;

        // IMPORTANT:
        // Do not show already delivered lots in Lot Approval modal.
        if (lockedFlags[index]) return;

        rows.push({
          key: `${item.SODetailID || item.ItemId}-${lotNumber}-${lot.ExpiryDate || ""}-${index}`,
          itemName,
          catalogNo,
          lotNumber,
          expiryDate: lot.ExpiryDate || "",
          warehouseName: lot.WarehouseName || "",
          qty: Number(lot.Qty || 1),
        });
      });
    });

    return rows;
  }, [items, deliveredMap]);

  const hasLotRowsForApproval = selectedLotRows.length > 0;

  const canShowApproveLotButton =
    canApproveLot && hasLotRowsForApproval;

  const formatTimelineDate = (value) => {
    if (!value) return "-";

    const d = new Date(value);

    if (Number.isNaN(d.getTime())) return String(value);

    return d.toLocaleString("en-PH", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getTimelineTitle = (row) => {
    const action = String(row.ActionType || "").toUpperCase();
    const actor = row.ActionBy || "SYSTEM";

    if (action === "LOT_INPUT") {
      const count = Number(row.LotCount || 0);
      return `${count} lot${count > 1 ? "s" : ""} inputted by ${actor}`;
    }

    if (action === "LOT_UPDATE") {
      const count = Number(row.LotCount || 0);
      return `${count} lot${count > 1 ? "s" : ""} updated by ${actor}`;
    }

    if (action === "LOT_APPROVAL") {
      return `Lots approved by ${actor}`;
    }

    if (action === "DN_CREATED") {
      return `Delivery Note ${row.DNNumbers || ""} created by ${actor}`;
    }

    if (action === "DN_CANCELLED") {
      return `Delivery Note ${row.DNNumbers || ""} voided by ${actor}`;
    }

    if (action === "DN_ITEM_DELIVERED") {
      return `${row.TotalDeliveredQty || 0} item(s) delivered by ${actor}`;
    }

    return `${row.ActionType || "Activity"} by ${actor}`;
  };

  const getTimelineBadgeClass = (type) => {
    const action = String(type || "").toUpperCase();

    if (action === "LOT_INPUT") return "timeline-badge lot-input";
    if (action === "LOT_UPDATE") return "timeline-badge lot-update";
    if (action === "LOT_APPROVAL") return "timeline-badge lot-approval";
    if (action === "DN_CREATED") return "timeline-badge dn-created";
    if (action === "DN_CANCELLED") return "timeline-badge dn-cancelled";
    if (action === "DN_ITEM_DELIVERED") return "timeline-badge dn-delivered";

    return "timeline-badge";
  };

  const openTimelineModal = async () => {
    if (!selectedSO) return;

    setSoTimeline([]);
    setShowTimelineModal(true);
    await loadSOTimeline(selectedSO.SOID || selectedSO.soid);
  };

  const closeTimelineModal = () => {
    setShowTimelineModal(false);
  };

  const renderTimelineContent = () => {
    if (timelineLoading) {
      return <div className="so-timeline-empty">Loading timeline...</div>;
    }

    if (soTimeline.length === 0) {
      return <div className="so-timeline-empty">No monitoring logs yet.</div>;
    }

    return (
      <div className="so-timeline-list">
        {soTimeline.map((row) => (
          <div
            className="so-timeline-row"
            key={`${row.ActionType}-${row.ActionGroupID || row.LogID}`}
          >
            <div className={getTimelineBadgeClass(row.ActionType)}>
              {String(row.ActionType || "").replace(/_/g, " ")}
            </div>

            <div className="so-timeline-body">
              <div className="so-timeline-main">
                <strong>{getTimelineTitle(row)}</strong>
                <span>{formatTimelineDate(row.ActionAt)}</span>
              </div>

              {row.LotNumbers ? (
                <div className="so-timeline-sub">Lots: {row.LotNumbers}</div>
              ) : null}

              {row.DNNumbers ? (
                <div className="so-timeline-sub">DN: {row.DNNumbers}</div>
              ) : null}

              {row.CatalogNumbers ? (
                <div className="so-timeline-sub">
                  Catalog No: {row.CatalogNumbers}
                </div>
              ) : null}

              {row.Remarks ? (
                <div className="so-timeline-remarks">{row.Remarks}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const closeSelectedSOModal = () => {
    setSelectedSO(null);
    setSoTimeline([]);
    setShowTimelineModal(false);
  };

  return (
    <div className="so-container">
      <div className="so-page-shell">
        <div className="so-topbar">
          <div>
            <p className="so-eyebrow">Operations / Logistics</p>
            <h2>Sales Orders Logistics</h2>
            <p className="so-subtitle">
              Review, assign lots, manage staggered batches, and generate print-ready copies.
            </p>
          </div>

          <div className="so-summary-card">
            <span className="summary-label">Total Orders</span>
            <strong>{totalRows}</strong>
          </div>
        </div>

        <div className="so-card">
          <div className="table-card-header logistics-table-card-header">
            <div>
              <h3>Sales Order Queue</h3>
              <p>Premium logistics view with batch and lot assignment workflow.</p>
            </div>
          </div>

          <div className="logistics-filter-bar">
            <div className="logistics-search-box">
              <input
                type="text"
                placeholder="Search SO number, customer, created by, ship to, order type..."
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
              />
            </div>

            <div className="logistics-filter-group">
              <label>Created From</label>
              <input
                type="date"
                value={createdDateFrom}
                onChange={(e) => setCreatedDateFrom(e.target.value)}
              />
            </div>

            <div className="logistics-filter-group">
              <label>Created To</label>
              <input
                type="date"
                value={createdDateTo}
                onChange={(e) => setCreatedDateTo(e.target.value)}
              />
            </div>

            {/* <div className="logistics-filter-group">
              <label>Created By</label>
              <select
                value={createdByFilter}
                onChange={(e) => setCreatedByFilter(e.target.value)}
              >
                <option value="ALL">All Created By</option>
                {createdByFilterOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div> */}

            <div className="logistics-filter-group">
              <label>Order Type</label>
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
              >
                <option value="ALL">All Order Type</option>
                <option value="NORMAL">Normal</option>
                <option value="DEMO">Demo</option>
                <option value="FREE_GOODS">Free Goods</option>
                <option value="BIOCARE_ACCOUNT">Biocare Account</option>
                <option value="REPLACEMENT">Replacement</option>
                <option value="AS_TESTING">AS Testing</option>
                <option value="TSD_TESTING">TSD Testing</option>
                <option value="MARKETING_EVENT">Marketing Event</option>
              </select>
            </div>

            <div className="logistics-filter-group">
              <label>Delivery Mode</label>
              <select
                value={deliveryModeFilter}
                onChange={(e) => setDeliveryModeFilter(e.target.value)}
              >
                <option value="ALL">All Delivery Mode</option>
                <option value="SINGLE">Single</option>
                <option value="STAGGERED">Staggered</option>
              </select>
            </div>

            <div className="logistics-filter-group">
              <label>Approval</label>
              <select
                value={approvalStatusFilter}
                onChange={(e) => setApprovalStatusFilter(e.target.value)}
              >
                <option value="ALL">All Approval</option>
                <option value="PENDING">Pending</option>
                <option value="ONHOLD">On Hold</option>
                <option value="APPROVED">Approved</option>
                {/* <option value="DISAPPROVED">Disapproved</option> */}
              </select>
            </div>

            <div className="logistics-filter-group">
              <label>Lot Approval</label>
              <select
                value={lotApprovalFilter}
                onChange={(e) => setLotApprovalFilter(e.target.value)}
              >
                <option value="ALL">All Lot Approval</option>
                <option value="PENDING">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            <div className="logistics-filter-group">
              <label>ALLOCATION</label>
              <select
                value={soStatusFilter}
                onChange={(e) => setSOStatusFilter(e.target.value)}
              >
                <option value="ALL">All SO Status</option>
                <option value="PENDING">Pending</option>
                <option value="ONGOING">Ongoing</option>
                {/* <option value="PARTIAL">Partial</option> */}
                <option value="COMPLETE">Complete</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="RETURNED">Returned</option>
              </select>
            </div>

            <div className="logistics-filter-group">
              <label>SO Status</label>
              <select
                value={deliveryStatusFilter}
                onChange={(e) => setDeliveryStatusFilter(e.target.value)}
              >
                <option value="ALL">All Delivery Status</option>
                <option value="PENDING">Pending</option>
                <option value="ONGOING">Ongoing</option>
                <option value="PARTIAL">Partial</option>
                <option value="COMPLETE">Complete</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="RETURNED">Returned</option>
              </select>
            </div>

            <button
              type="button"
              className="logistics-clear-filter-btn"
              onClick={() => {
                setTableSearch("");
                setCreatedDateFrom("");
                setCreatedDateTo("");
                setCreatedByFilter("ALL");
                setApprovalStatusFilter("ALL");
                setLotApprovalFilter("ALL");
                setSOStatusFilter("ALL");
                setDeliveryStatusFilter("ALL");
                setOrderTypeFilter("ALL");
                setDeliveryModeFilter("ALL");
              }}
            >
              Clear
            </button>

            <div className="logistics-filter-group rows-filter">
              <label>Rows</label>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={20}>20</option>
              </select>
            </div>
          </div>

          <div className="table-wrap">
            <table className="so-table">
              <thead>
                <tr>
                  <th>SO Number</th>
                  <th>Customer</th>
                  {/* <th>Created By</th> */}
                  <th>Date</th>
                  <th>Order Type</th>
                  <th>Delivery Mode</th>
                  <th>Approval Status</th>
                  <th>Allocation</th>
                  <th>Lot Approval</th>
                  <th>SO Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {pagedSalesOrders.map((so) => {
                  const currentSOStatus =
                    so.sostatus ||
                    so.SOStatus ||
                    "PENDING";

                  const currentDeliveryStatus =
                    so.deliverystatus ||
                    so.DeliveryStatus ||
                    "PENDING";

                  const currentLotApprovalStatus =
                    so.lotapprovalstatus ||
                    so.LotApprovalStatus ||
                    so.lotApprovalStatus ||
                    "PENDING";

                  return (
                    <tr key={so.soid || so.SOID}>
                      <td>
                        <div className="so-number-box">
                          <span className="so-number-main">{so.sonumber || so.SONumber}</span>
                          <span className="so-number-sub">Sales Order</span>
                        </div>
                      </td>

                      <td
                        className="logistics-customer-td"
                        title={so.customername || so.CustomerName || "-"}
                      >
                        <button
                          type="button"
                          className="logistics-customer-link"
                          onClick={() => openCustomerDetailsModal(so)}
                        >
                          <span className="logistics-customer-name">
                            {so.customername || so.CustomerName || "-"}
                          </span>
                          <span className="logistics-customer-view-text">
                            View customer details
                          </span>
                        </button>
                      </td>
                      {/* 
                      <td>{getSOCreatedByName(so)}</td> */}

                      <td className="so-date-cell">
                        <span className="so-date-text">{formatSODateTime(so)}</span>
                      </td>

                      <td>{orderTypeLabel(so.ordertype || so.OrderType)}</td>

                      <td>{deliveryModeLabel(so.deliverymode || so.DeliveryMode)}</td>

                      <td>
                        <span
                          className={getStatusClass(so.approvalstatus || so.ApprovalStatus)}
                        >
                          {approvalStatusLabel(so.approvalstatus || so.ApprovalStatus)}
                        </span>
                      </td>

                      <td>
                        <span className={getStatusClass(currentSOStatus)}>
                          {soStatusLabel(currentSOStatus)}
                        </span>
                      </td>

                      <td>
                        <span className={getStatusClass(currentLotApprovalStatus)}>
                          {lotApprovalStatusLabel(currentLotApprovalStatus)}
                        </span>
                      </td>

                      <td>
                        <span className={getStatusClass(currentDeliveryStatus)}>
                          {soStatusLabel(currentDeliveryStatus)}
                        </span>
                      </td>

                      <td>
                        <button
                          className="btn-view"
                          onClick={() => viewSO(so.soid || so.SOID)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {pagedSalesOrders.length === 0 && (
                  <tr>
                    <td colSpan={10} className="empty-state-cell">
                      No sales orders found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-bar">
            <div className="pagination-info">Showing {pageInfo}</div>

            <div className="pagination-controls">
              <button className="btn-page" onClick={() => setPage(1)} disabled={page <= 1}>
                {"<<"}
              </button>

              <button
                className="btn-page"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                Prev
              </button>

              <div className="pagination-page">
                Page <b>{page}</b> / <b>{totalPages}</b>
              </div>

              <button
                className="btn-page"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                Next
              </button>

              <button
                className="btn-page"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages}
              >
                {">>"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showCustomerDetailsModal && selectedCustomerDetails && (
        <div className="modal-overlay">
          <div className="modal-content customer-details-modal logistics-customer-details-modal">
            <div className="customer-details-header">
              <div>
                <span className="customer-details-kicker">Customer Profile</span>
                <h3>
                  {getValue(selectedCustomerDetails, [
                    "customer_name",
                    "CustomerName",
                    "customername"
                  ])}
                </h3>
                <p>Full customer information connected to this Sales Order.</p>
              </div>

              <button
                type="button"
                className="customer-details-close"
                onClick={closeCustomerDetailsModal}
              >
                ×
              </button>
            </div>

            <div className="customer-details-grid">
              <div className="customer-detail-box">
                <span>Customer Number</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "customer_number",
                    "CustomerNumber",
                    "Customer_Number"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Customer Name</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "customer_name",
                    "CustomerName",
                    "customername"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Terms</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "terms",
                    "Terms",
                    "terms_of_payment",
                    "TermsOfPayment"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Area Manager Code</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "area_manager_code",
                    "Area_Manager_Code",
                    "AreaManagerCode"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box customer-detail-full">
                <span>Ship To</span>
                <strong>
                  {getValue(selectedCustomerDetails, ["ship_to", "ShipTo", "shipto"])}
                </strong>
              </div>

              <div className="customer-detail-box customer-detail-full">
                <span>Address</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "address",
                    "Address",
                    "customer_address",
                    "CustomerAddress"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Contact Person</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "contact_person",
                    "ContactPerson",
                    "Contact_Person"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Contact Number</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "contact_number",
                    "ContactNumber",
                    "contact_no",
                    "ContactNo",
                    "phone",
                    "Phone"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>Email</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "email",
                    "Email",
                    "email_address",
                    "EmailAddress"
                  ])}
                </strong>
              </div>

              <div className="customer-detail-box">
                <span>TIN</span>
                <strong>
                  {getValue(selectedCustomerDetails, [
                    "tin",
                    "TIN",
                    "tax_identification_number"
                  ])}
                </strong>
              </div>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={closeCustomerDetailsModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedSO && !showPrintPreview && (
        <div className="modal-overlay">
          <div className="modal-content form-modal">
            <div className="modal-header">
              <div>
                <p className="modal-label">Logistics Entry</p>
                <h3>
                  <strong>{selectedSO.SONumber}</strong>
                </h3>
              </div>

              <div className="modal-badges">
                <span className="soft-badge">
                  {deliveryModeLabel(selectedSO.DeliveryMode || selectedSO.deliverymode)}
                </span>

                <span
                  className={getStatusClass(
                    selectedSO.ApprovalStatus || selectedSO.approvalstatus
                  )}
                >
                  {approvalStatusLabel(
                    selectedSO.ApprovalStatus || selectedSO.approvalstatus
                  )}
                </span>

                <span className={getStatusClass(selectedLotApprovalStatus)}>
                  Lot: {lotApprovalStatusLabel(selectedLotApprovalStatus)}
                </span>

                <span
                  className={getStatusClass(
                    selectedSO.SOStatus ||
                    selectedSO.sostatus ||
                    selectedSO.DeliveryStatus ||
                    selectedSO.deliverystatus
                  )}
                >
                  {soStatusLabel(
                    selectedSO.SOStatus ||
                    selectedSO.sostatus ||
                    selectedSO.DeliveryStatus ||
                    selectedSO.deliverystatus
                  )}
                </span>
              </div>
            </div>

            {selectedSO && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  marginBottom: 14,
                  padding: 12,
                  border: "1px solid #e8e8e8",
                  borderRadius: 10,
                  background: "#fafafa"
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: "#666" }}>Approval Status</div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>
                    {approvalStatusLabel(
                      selectedSO.ApprovalStatus || selectedSO.approvalstatus
                    )}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#666" }}>Approved By</div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>
                    {selectedSO.ApprovedBy || selectedSO.approvedby || "-"}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#666" }}>Approved At</div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>
                    {selectedSO.ApprovedAt || selectedSO.approvedat
                      ? new Date(selectedSO.ApprovedAt || selectedSO.approvedat).toLocaleString()
                      : "-"}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: 12, color: "#666" }}>Remarks</div>
                  <div style={{ marginTop: 6, fontWeight: 700 }}>
                    {selectedSO.ApprovalRemarks || selectedSO.approvalremarks || "-"}
                  </div>
                </div>
              </div>
            )}

            <div className="so-monitoring-launch">
              <div className="so-monitoring-launch-left">
                <div className="so-monitoring-icon">⏱</div>

                <div>
                  <p className="so-timeline-kicker">Monitoring</p>
                  <h4>Sales Order Timeline</h4>
                  <span>View lot, approval, and delivery note activity</span>
                </div>
              </div>

              <button
                type="button"
                className="so-monitoring-open-btn"
                onClick={openTimelineModal}
              >
                View Timeline
              </button>
            </div>

            {(isLocked || isApprovalLocked || isRole8PrintPreviewOnly) && (
              <div className="locked-banner">
                {isRole8PrintPreviewOnly
                  ? "PRINT PREVIEW ONLY — Editing is disabled for this role."
                  : lockedReason || "Locked — Editing is disabled."}
              </div>
            )}

            {!isPrintPreviewOnlyRole && isSelectedStaggered && (
              <div className="batch-box">
                <div className="batch-row">
                  <div className="batch-row-left">
                    <b className="batch-title">STAGGERED DELIVERY BATCHES</b>
                    <span className="batch-desc">
                      Choose a batch and assign lots only for the selected delivery.
                    </span>
                  </div>

                  <div className="batch-control">
                    <label className="batch-label">Batch</label>
                    <select
                      value={selectedBatchId}
                      onChange={(e) => handleSelectBatch(e.target.value)}
                      className="batch-select"
                      disabled={isLocked || isApprovalLocked}
                    >
                      <option value="">-- Select Batch --</option>
                      {batches.map((b) => (
                        <option key={b.BatchID} value={String(b.BatchID)}>
                          Batch #{b.BatchNo} ({b.PlannedMonth || "no month"}) - {b.Status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {selectedBatch ? (
                  <div className="batch-selected">
                    Selected: Batch #{selectedBatch.BatchNo} •{" "}
                    {selectedBatch.PlannedMonth || "-"}
                  </div>
                ) : null}

                <div className="batch-tip">
                  Tip: Set <b>Batch Qty</b> per item, then assign Lot # for that batch only.
                </div>
              </div>
            )}

            {!isPrintPreviewOnlyRole && (
              <div className="items-stack">
                {items.map((item) =>
                  isSelectedStaggered ? (
                    <LotEditorBatch
                      key={item.SODetailID}
                      item={item}
                      selectedSOID={selectedSO.SOID || selectedSO.soid}
                      isEditing={canEditLots}
                      onBatchQtyChange={handleBatchQtyChange}
                      onUpdateLots={handleLotUpdate}
                      deliveredMap={deliveredMap}
                    />
                  ) : (
                    <LotEditorSingle
                      key={item.SODetailID}
                      item={item}
                      selectedSOID={selectedSO.SOID || selectedSO.soid}
                      isEditing={canEditLots}
                      onUpdateLots={handleLotUpdate}
                      deliveredMap={deliveredMap}
                    />
                  )
                )}
              </div>
            )}

            {canSeeLotApproval && (
              <div className="lot-approval-panel">
                <div>
                  <div className="lot-approval-label">Lot Approval</div>

                  <span className={getStatusClass(selectedLotApprovalStatus)}>
                    {lotApprovalStatusLabel(selectedLotApprovalStatus)}
                  </span>

                  {selectedSO?.LotApprovedBy || selectedSO?.lotapprovedby ? (
                    <div className="lot-approval-meta">
                      Approved by: {selectedSO?.LotApprovedBy || selectedSO?.lotapprovedby}
                    </div>
                  ) : null}
                </div>

                {canShowApproveLotButton && (
                  <button
                    type="button"
                    className="btn-save"
                    onClick={handleApproveLots}
                    disabled={lotApprovalLoading}
                  >
                    {lotApprovalLoading ? "Approving..." : "Approve Lot #"}
                  </button>
                )}
              </div>
            )}

            <div className="modal-actions">
              {isRole8PrintPreviewOnly ? (
                <>
                  <button className="btn-print" onClick={openPrintPreview}>
                    Print Preview
                  </button>

                  <button className="btn-cancel" onClick={closeSelectedSOModal}>
                    Close
                  </button>
                </>
              ) : isCancelledSO(selectedSO) ? (
                <button className="btn-cancel" onClick={closeSelectedSOModal}>
                  Close
                </button>
              ) : isLocked || isApprovalLocked ? (
                <>
                  <button className="btn-print" onClick={openPrintPreview}>
                    Print Preview
                  </button>

                  <button className="btn-cancel" onClick={closeSelectedSOModal}>
                    Close
                  </button>
                </>
              ) : isEditing ? (
                <>
                  <button className="btn-save" onClick={saveChanges}>
                    Save
                  </button>

                  <button className="btn-clear" onClick={clearLots}>
                    Clear Lots
                  </button>

                  <button className="btn-cancel" onClick={closeSelectedSOModal}>
                    Close
                  </button>
                </>
              ) : (
                <>
                  <button className="btn-edit" onClick={() => setIsEditing(true)}>
                    Edit
                  </button>

                  <button className="btn-print" onClick={openPrintPreview}>
                    Print Preview
                  </button>

                  <button className="btn-cancel" onClick={closeSelectedSOModal}>
                    Close
                  </button>
                </>

              )}
            </div>
          </div>
        </div>
      )}

      {showTimelineModal && selectedSO && (
        <div className="modal-overlay timeline-modal-overlay">
          <div className="modal-content so-timeline-modal">
            <div className="so-timeline-modal-head">
              <div>
                <p className="so-timeline-kicker">Monitoring</p>
                <h3>Sales Order Timeline</h3>
                <span>{selectedSO.SONumber || selectedSO.sonumber || "-"}</span>
              </div>

              <div className="so-timeline-modal-actions">
                <button
                  type="button"
                  className="so-timeline-refresh"
                  onClick={() => loadSOTimeline(selectedSO.SOID || selectedSO.soid)}
                  disabled={timelineLoading}
                >
                  {timelineLoading ? "Loading..." : "Refresh"}
                </button>

                <button
                  type="button"
                  className="so-timeline-close-btn"
                  onClick={closeTimelineModal}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="so-timeline-modal-body">
              {renderTimelineContent()}
            </div>
          </div>
        </div>
      )}

      {showLotApprovalConfirm && selectedSO && (
        <div className="modal-overlay lot-confirm-overlay">
          <div className="lot-confirm-modal">

            <div className="lot-confirm-content">
              <p className="lot-confirm-kicker">Lot Approval</p>

              <h3>Approve Lot # for this Sales Order?</h3>

              <p>
                Once approved, this Sales Order can proceed to Delivery Note creation.
              </p>

              <div className="lot-confirm-so-box">
                <span>Sales Order</span>
                <strong>{selectedSO.SONumber || selectedSO.sonumber || "-"}</strong>
              </div>

              <div className="lot-confirm-lots-box">
                <div className="lot-confirm-lots-head">
                  <span>Lot Numbers for Approval</span>
                  <strong>{selectedLotRows.length} row(s)</strong>
                </div>

                {selectedLotRows.length > 0 ? (
                  <div className="lot-confirm-lots-list">
                    {selectedLotRows.map((row) => (
                      <div className="lot-confirm-lot-row" key={row.key}>
                        <div>
                          <strong>{row.lotNumber}</strong>
                          <span>
                            {row.catalogNo ? `${row.catalogNo} • ` : ""}
                            {row.itemName}
                          </span>
                        </div>

                        <div className="lot-confirm-lot-meta">
                          <span>Qty: {row.qty}</span>
                          {row.expiryDate ? <span>Exp: {String(row.expiryDate).slice(0, 10)}</span> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="lot-confirm-empty">
                    No remaining Lot # for approval.
                  </div>
                )}
              </div>
            </div>

            <div className="lot-confirm-actions">
              <button
                type="button"
                className="btn-save"
                onClick={confirmApproveLots}
                disabled={lotApprovalLoading}
              >
                {lotApprovalLoading ? "Approving..." : "Approve Lot #"}
              </button>
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowLotApprovalConfirm(false)}
                disabled={lotApprovalLoading}
              >
                Cancel
              </button>

            </div>
          </div>
        </div>
      )}

      {selectedSO && showPrintPreview && (
        <div className="modal-overlay">
          <div className="modal-content print-modal">
            <div ref={printRef}>
              <SoPrintTemplateLogistics so={{ ...selectedSO, items, selectedBatch }} />
            </div>

            <div className="modal-actions">
              <button className="btn-print" onClick={downloadThreeCopiesPDF}>
                Download PDF
              </button>

              <button className="btn-cancel" onClick={() => setShowPrintPreview(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const getLotOptionValue = (lot) => {
  return `${lot.LotNumber || ""}||${lot.WarehouseID || ""}||${lot.ExpiryDate || ""}`;
};

const getSelectedLotValue = (row) => {
  if (!row?.LotNumber) return "";
  return `${row.LotNumber || ""}||${row.WarehouseID || ""}||${row.ExpiryDate || ""}`;
};

const getLotOptionLabel = (lot) => {
  const availableQty = Number(lot.AvailableQuantity || 0);
  const reservedQty = Number(lot.ReservedQuantity || 0);
  const totalQty = Number(lot.TotalQuantity || 0);
  const reservedRefs = String(lot.ReservedSORefs || "").trim();

  return `${lot.LotNumber} (${lot.WarehouseName || "No warehouse"} | Avail: ${availableQty} | Reserved: ${reservedQty} | Total: ${totalQty}${reservedRefs ? ` | Used in: ${reservedRefs}` : ""
    })`;
};

const LotEditorSingle = ({
  item,
  selectedSOID,
  onUpdateLots,
  isEditing,
  deliveredMap,
}) => {
  const [rows, setRows] = useState(item.lots || []);
  const [stockLots, setStockLots] = useState([]);

  useEffect(() => {
    setRows(item.lots || []);
  }, [item.lots]);

  const loadStockLots = useCallback(
    async (itemId) => {
      try {
        const data = await getLotsByItem(itemId, 0, selectedSOID || 0);
        setStockLots(data || []);
      } catch (err) {
        console.error("Error loading lot options:", err);
      }
    },
    [selectedSOID]
  );

  useEffect(() => {
    if (!item?.ItemId) return;
    loadStockLots(item.ItemId);
  }, [item.ItemId, loadStockLots]);

  const lockedFlags = getLockedRowFlags(item.SODetailID, rows, deliveredMap);

  useEffect(() => {
    if (!stockLots.length) return;

    setRows((prev) => {
      let changed = false;

      const next = (prev || []).map((r) => {
        if (!r?.LotNumber && (r?.ExpiryDate || r?.WarehouseID || r?.WarehouseName)) {
          changed = true;

          return {
            ...r,
            ExpiryDate: "",
            WarehouseID: "",
            WarehouseName: "",
          };
        }

        if (r?.LotNumber && !r?.WarehouseID) {
          const m = stockLots.find(
            (s) =>
              String(s.LotNumber) === String(r.LotNumber) &&
              (!r.ExpiryDate || String(s.ExpiryDate) === String(r.ExpiryDate))
          );

          if (m) {
            changed = true;

            return {
              ...r,
              WarehouseID: m.WarehouseID,
              WarehouseName: m.WarehouseName,
              ExpiryDate: m.ExpiryDate || r.ExpiryDate || "",
            };
          }
        }

        return r;
      });

      if (changed) {
        onUpdateLots(item.SODetailID, next);
      }

      return next;
    });
  }, [stockLots, item.SODetailID, onUpdateLots]);

  const updateRow = (index, field, value) => {
    if (!isEditing) return;
    if (lockedFlags[index]) return;

    const updated = [...rows];

    updated[index] = {
      ...(updated[index] || {}),
      [field]: value,
    };

    if (field === "LotNumber") {
      if (!value) {
        updated[index] = {
          ...(updated[index] || {}),
          LotNumber: "",
          ExpiryDate: "",
          WarehouseID: "",
          WarehouseName: "",
          Qty: Number(updated[index]?.Qty || 1),
        };

        setRows(updated);
        onUpdateLots(item.SODetailID, updated);
        return;
      }

      const [lotNo, wid, exp] = String(value || "").split("||");

      const match = stockLots.find(
        (l) =>
          String(l.LotNumber) === String(lotNo) &&
          String(l.WarehouseID) === String(wid) &&
          String(l.ExpiryDate || "") === String(exp || "")
      );

      if (match) {
        updated[index] = {
          ...(updated[index] || {}),
          LotNumber: match.LotNumber,
          ExpiryDate: match.ExpiryDate || "",
          WarehouseID: match.WarehouseID,
          WarehouseName: match.WarehouseName || "",
          Qty: Number(updated[index]?.Qty || 1),
        };
      }
    }

    if (field === "Qty") {
      updated[index] = {
        ...(updated[index] || {}),
        Qty: Math.max(1, Number(value || 1)),
      };
    }

    setRows(updated);
    onUpdateLots(item.SODetailID, updated);
  };

  return (
    <div className="lot-block">
      <div className="lot-block-head">
        <h4>{item.ItemName || item.CurrentItemName || "-"}</h4>

        <span className="lot-meta">
          Required: {Number(item.Quantity || 0)} {unitLabel(item.UnitOfMeasure)}
        </span>
      </div>

      <div className="lot-table-wrap">
        <table className="lot-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Lot #</th>
              <th>Expiry Date</th>
              {/* <th>Warehouse</th> */}
              <th>Qty</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {(rows || []).map((row, idx) => {
              const locked = lockedFlags[idx];

              return (
                <tr key={`${item.SODetailID}-${idx}`}>
                  <td>{idx + 1}</td>

                  <td>
                    <select
                      value={getSelectedLotValue(row)}
                      disabled={!isEditing || locked}
                      onChange={(e) => updateRow(idx, "LotNumber", e.target.value)}
                    >
                      <option value="">Select Lot #</option>

                      {stockLots.map((lot, lotIdx) => (
                        <option
                          key={`${lot.LotNumber}-${lot.WarehouseID}-${lot.ExpiryDate}-${lotIdx}`}
                          value={getLotOptionValue(lot)}
                        >
                          {getLotOptionLabel(lot)}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>{row.ExpiryDate || "-"}</td>

                  {/* <td>{row.WarehouseName || "-"}</td> */}

                  <td>
                    <input
                      type="number"
                      min="1"
                      value={row.Qty || 1}
                      disabled={!isEditing || locked}
                      onChange={(e) => updateRow(idx, "Qty", e.target.value)}
                    />
                  </td>

                  <td>
                    {locked ? (
                      <span className="status-pill complete">Delivered</span>
                    ) : row.LotNumber ? (
                      <span className="status-pill ongoing">Reserved</span>
                    ) : (
                      <span className="status-pill pending">Blank</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {(!rows || rows.length === 0) && (
              <tr>
                <td colSpan={6} className="empty-state-cell">
                  No lot rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const LotEditorBatch = ({
  item,
  selectedSOID,
  onUpdateLots,
  isEditing,
  onBatchQtyChange,
  deliveredMap,
}) => {
  const [rows, setRows] = useState(item.lots || []);
  const [stockLots, setStockLots] = useState([]);

  useEffect(() => {
    setRows(item.lots || []);
  }, [item.lots]);

  const loadStockLots = useCallback(
    async (itemId) => {
      try {
        const data = await getLotsByItem(itemId, 0, selectedSOID || 0);
        setStockLots(data || []);
      } catch (err) {
        console.error("Error loading lot options:", err);
      }
    },
    [selectedSOID]
  );

  useEffect(() => {
    if (!item?.ItemId) return;
    loadStockLots(item.ItemId);
  }, [item.ItemId, loadStockLots]);

  const lockedFlags = getLockedRowFlags(item.SODetailID, rows, deliveredMap);

  useEffect(() => {
    if (!stockLots.length) return;

    setRows((prev) => {
      let changed = false;

      const next = (prev || []).map((r) => {
        if (!r?.LotNumber && (r?.ExpiryDate || r?.WarehouseID || r?.WarehouseName)) {
          changed = true;

          return {
            ...r,
            ExpiryDate: "",
            WarehouseID: "",
            WarehouseName: "",
          };
        }

        if (r?.LotNumber && !r?.WarehouseID) {
          const m = stockLots.find(
            (s) =>
              String(s.LotNumber) === String(r.LotNumber) &&
              (!r.ExpiryDate || String(s.ExpiryDate) === String(r.ExpiryDate))
          );

          if (m) {
            changed = true;

            return {
              ...r,
              WarehouseID: m.WarehouseID,
              WarehouseName: m.WarehouseName,
              ExpiryDate: m.ExpiryDate || r.ExpiryDate || "",
            };
          }
        }

        return r;
      });

      if (changed) {
        onUpdateLots(item.SODetailID, next);
      }

      return next;
    });
  }, [stockLots, item.SODetailID, onUpdateLots]);

  const updateRow = (index, field, value) => {
    if (!isEditing) return;
    if (lockedFlags[index]) return;

    const updated = [...rows];

    updated[index] = {
      ...(updated[index] || {}),
      [field]: value,
    };

    if (field === "LotNumber") {
      if (!value) {
        updated[index] = {
          ...(updated[index] || {}),
          LotNumber: "",
          ExpiryDate: "",
          WarehouseID: "",
          WarehouseName: "",
          Qty: Number(updated[index]?.Qty || 1),
        };

        setRows(updated);
        onUpdateLots(item.SODetailID, updated);
        return;
      }

      const [lotNo, wid, exp] = String(value || "").split("||");

      const match = stockLots.find(
        (l) =>
          String(l.LotNumber) === String(lotNo) &&
          String(l.WarehouseID) === String(wid) &&
          String(l.ExpiryDate || "") === String(exp || "")
      );

      if (match) {
        updated[index] = {
          ...(updated[index] || {}),
          LotNumber: match.LotNumber,
          ExpiryDate: match.ExpiryDate || "",
          WarehouseID: match.WarehouseID,
          WarehouseName: match.WarehouseName || "",
          Qty: Number(updated[index]?.Qty || 1),
        };
      }
    }

    if (field === "Qty") {
      updated[index] = {
        ...(updated[index] || {}),
        Qty: Math.max(1, Number(value || 1)),
      };
    }

    setRows(updated);
    onUpdateLots(item.SODetailID, updated);
  };

  const fullQty = Number(item.Quantity || 0);
  const batchQty = Number(item.batchQty || 0);

  const deliveredMinQty = lockedFlags.reduce((sum, locked, idx) => {
    if (!locked) return sum;
    return sum + Number(rows?.[idx]?.Qty || 1);
  }, 0);

  const handleBatchQtySafe = (value) => {
    const nextQty = Number(value || 0);

    if (nextQty < deliveredMinQty) {
      alert(`Cannot set Batch Qty below delivered qty (${deliveredMinQty}).`);
      return;
    }

    onBatchQtyChange(item.SODetailID, nextQty);
  };

  return (
    <div className="lot-block">
      <div className="lot-block-head">
        <h4>{item.ItemName || item.CurrentItemName || "-"}</h4>

        <span className="lot-meta">
          Required: {fullQty} {unitLabel(item.UnitOfMeasure)}
        </span>
      </div>

      <div className="batch-qty-row">
        <label>Batch Qty</label>

        <input
          type="number"
          min={deliveredMinQty}
          max={fullQty}
          value={batchQty}
          disabled={!isEditing}
          onChange={(e) => handleBatchQtySafe(e.target.value)}
        />

        <span className="batch-qty-help">
          Delivered locked qty: {deliveredMinQty}
        </span>
      </div>

      <div className="lot-table-wrap">
        <table className="lot-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Lot #</th>
              <th>Expiry Date</th>
              <th>Warehouse</th>
              <th>Qty</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {(rows || []).map((row, idx) => {
              const locked = lockedFlags[idx];

              return (
                <tr key={`${item.SODetailID}-${idx}`}>
                  <td>{idx + 1}</td>

                  <td>
                    <select
                      value={getSelectedLotValue(row)}
                      disabled={!isEditing || locked}
                      onChange={(e) => updateRow(idx, "LotNumber", e.target.value)}
                    >
                      <option value="">Select Lot #</option>

                      {stockLots.map((lot, lotIdx) => (
                        <option
                          key={`${lot.LotNumber}-${lot.WarehouseID}-${lot.ExpiryDate}-${lotIdx}`}
                          value={getLotOptionValue(lot)}
                        >
                          {getLotOptionLabel(lot)}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td>{row.ExpiryDate || "-"}</td>

                  <td>{row.WarehouseName || "-"}</td>

                  <td>
                    <input
                      type="number"
                      min="1"
                      value={row.Qty || 1}
                      disabled={!isEditing || locked}
                      onChange={(e) => updateRow(idx, "Qty", e.target.value)}
                    />
                  </td>

                  <td>
                    {locked ? (
                      <span className="status-pill complete">Delivered</span>
                    ) : row.LotNumber ? (
                      <span className="status-pill ongoing">Reserved</span>
                    ) : (
                      <span className="status-pill pending">Blank</span>
                    )}
                  </td>
                </tr>
              );
            })}

            {(!rows || rows.length === 0) && (
              <tr>
                <td colSpan={6} className="empty-state-cell">
                  Set Batch Qty to create lot rows.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
